import { useEffect, useState } from 'react'
import { refresh, type RefreshUnlockMaterial } from '../api/authApi'
import { sessionStore, type VaultUnlockMaterial } from '../services/sessionStore'

/**
 * アプリ起動 (初回ロード / F5 / タブ再読込) 時に 1 度だけ走るセッション復元 (B5)。
 *
 * in-memory の sessionStore は再読込で必ず UNAUTH に戻るが、HttpOnly の refresh
 * cookie はブラウザに残る。起動時に `POST /auth/refresh` を呼び、成功すれば
 * accessToken + 封印済 unlock 素材で **LOCKED 状態を復元**する。これにより F5 後も
 * 再ログイン不要で、マスターパスワード入力 (ロック解除) だけで操作を続けられる。
 *
 * refresh cookie が無い / 失効していれば復元せず UNAUTH のまま (RouteGuard が
 * #login へ誘導)。
 *
 * `bootPromise` でモジュール単位に 1 回化している — React StrictMode の effect
 * 二重起動で `refresh()` が 2 回走ると、refresh rotation の reuse 検知が誤発火して
 * 全セッションが失効してしまうため。
 */

let bootPromise: Promise<void> | null = null

/** サーバの `RefreshUnlockMaterial` を sessionStore の `VaultUnlockMaterial` に変換。 */
const toUnlockMaterial = (m: RefreshUnlockMaterial): VaultUnlockMaterial => ({
  kdfSalt: m.kdf.salt,
  kdfParams: {
    memoryCost: m.kdf.memoryCost,
    timeCost: m.kdf.timeCost,
    parallelism: m.kdf.parallelism,
  },
  protectedSymmetricKey: m.protectedSymmetricKey,
  protectedSymmetricKeyIv: m.protectedSymmetricKeyIv,
  email: m.email,
})

const doBoot = async (): Promise<void> => {
  if (sessionStore.getState().kind !== 'unauth') return
  try {
    const res = await refresh()
    if (res.material && sessionStore.getState().kind === 'unauth') {
      sessionStore.restoreLocked(res.accessToken, toUnlockMaterial(res.material))
    }
  } catch {
    // refresh cookie 無し / 失効 → 復元せず UNAUTH のまま
  }
}

/** 起動時セッション復元が完了したら true。完了するまで App は描画を保留する。 */
export const useBootSession = (): boolean => {
  // 既にセッションがある (= 通常のアプリ内遷移 / テストの事前 setup) なら復元不要で即 true。
  const [booted, setBooted] = useState(() => sessionStore.getState().kind !== 'unauth')

  useEffect(() => {
    if (sessionStore.getState().kind !== 'unauth') return undefined

    let cancelled = false

    bootPromise ??= doBoot()
    void bootPromise.finally(() => {
      if (!cancelled) setBooted(true)
    })

    return () => {
      cancelled = true
    }
  }, [])

  return booted
}
